import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { onGetPensionHistoryAsync } from './PensionHistory.slice'
import { getPensionConfig } from '../OnboardingPension/OnboardingPension.slice'
import Spinner from '../../components/Spinners/Spinner'
import { default as Button } from '../../components/Buttons/BaseButton'
import useIsMobile from '../../components/Layouts/Viewport'
import { formatCurrency } from '../../config/resources/utils'
import routes from '../../config/settings/routes'
import * as S from './PensionHistory.styles'

const formatAxisCurrency = (value) => formatCurrency(Number(value) || 0)

/** Compact Y-axis labels for narrow screens (e.g. $245k, $1,2M). */
const formatCompactAxisCurrency = (value) => {
  const amount = Number(value) || 0
  if (amount >= 1_000_000) {
    const millions = amount / 1_000_000
    const formatted = millions
      .toFixed(millions >= 10 ? 0 : 1)
      .replace('.', ',')
    return `$${formatted}M`
  }
  if (amount >= 1_000) {
    return `$${Math.round(amount / 1_000)}k`
  }
  return `$${amount}`
}

const shortMonthLabel = (label) => {
  if (!label) return ''
  return String(label).split(/\s+/)[0]
}

const getFiniteAmounts = (data) =>
  data
    .filter((point) => point.amount != null && point.amount !== '')
    .map((point) => Number(point.amount))
    .filter((amount) => Number.isFinite(amount))

/**
 * Fit the Y axis tightly to real pension amounts only.
 * Important: ignore null months — Number(null) === 0 and would flatten the chart to $0.
 */
const getYAxisDomain = (data) => {
  const amounts = getFiniteAmounts(data)

  if (!amounts.length) return [0, 'auto']

  const minAmount = Math.min(...amounts)
  const maxAmount = Math.max(...amounts)
  const mid = (minAmount + maxAmount) / 2
  const span = maxAmount - minAmount

  // Tight padding around the real range. Only invent a band when every
  // point is the same value (otherwise small increases look flat).
  const padding =
    span === 0
      ? Math.max(Math.abs(mid) * 0.03, 1_000)
      : Math.max(span * 0.25, 500)

  let domainMin = minAmount - padding
  let domainMax = maxAmount + padding

  if (minAmount >= 0 && domainMin < 0) {
    domainMin = 0
  }

  const niceStep = (range) => {
    if (range <= 0) return 1_000
    const rough = range / 4
    const power = 10 ** Math.floor(Math.log10(rough))
    const normalized = rough / power
    if (normalized <= 1) return power
    if (normalized <= 2) return 2 * power
    if (normalized <= 5) return 5 * power
    return 10 * power
  }

  const step = niceStep(domainMax - domainMin)
  domainMin = Math.floor(domainMin / step) * step
  domainMax = Math.ceil(domainMax / step) * step

  if (domainMin === domainMax) {
    domainMax = domainMin + step
  }

  if (minAmount >= 0 && domainMin < 0) {
    domainMin = 0
  }

  return [domainMin, domainMax]
}

const PensionTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null

  const point = payload[0]?.payload
  if (!point?.hasData) {
    return (
      <S.ChartTooltip>
        <S.ChartTooltipLabel>{point?.label}</S.ChartTooltipLabel>
        <S.ChartTooltipValue>Sin dato</S.ChartTooltipValue>
      </S.ChartTooltip>
    )
  }

  const statusLabel = point.isEstimated
    ? 'Estimado'
    : point.status === 'payed'
      ? 'Pagado'
      : 'Calculado'

  return (
    <S.ChartTooltip>
      <S.ChartTooltipLabel>
        {point.label} · {statusLabel}
      </S.ChartTooltipLabel>
      <S.ChartTooltipValue>{formatCurrency(point.amount)}</S.ChartTooltipValue>
    </S.ChartTooltip>
  )
}

const PensionHistory = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useIsMobile()
  const { history = [], loading, error } = useSelector(
    (state) => state.pensionHistory
  )
  const { config, fetchStatus: configFetchStatus } = useSelector(
    (state) => state.adminPensionConfig
  )
  const lastPensionValue = useSelector(
    (state) => state.dashboard?.dashboard?.lastPensionValue
  )

  const primary = theme.colors.action.primary.bg
  const muted = theme.colors.textMuted
  const grid = theme.colors.borderDefault
  const chartData = useMemo(() => history, [history])
  const hasPensionData = useMemo(
    () => getFiniteAmounts(chartData).length > 0,
    [chartData]
  )
  const yDomain = useMemo(() => getYAxisDomain(chartData), [chartData])

  const configReady =
    configFetchStatus === 'succeeded' || configFetchStatus === 'failed'
  const needsSetup = configReady && !config

  useEffect(() => {
    dispatch(onGetPensionHistoryAsync())
    dispatch(getPensionConfig())
  }, [dispatch])

  // Refetch when dashboard obtains a pension amount (e.g. after first generation).
  useEffect(() => {
    if (lastPensionValue == null || lastPensionValue === '') return
    if (!Number.isFinite(Number(lastPensionValue))) return
    dispatch(onGetPensionHistoryAsync())
  }, [dispatch, lastPensionValue])

  const goToPreferences = () => {
    navigate(`${routes.preferences}#metodo-de-calculo`)
  }

  const chartMargin = isMobile
    ? { top: 8, right: 4, left: 0, bottom: 8 }
    : { top: 12, right: 8, left: 4, bottom: 0 }

  return (
    <S.ChartCard>
      <S.ChartHeader>
        <S.ChartSubtitle>
          Últimos 12 meses · montos en CLP · incluye proyección estimada
        </S.ChartSubtitle>
      </S.ChartHeader>

      {loading && <Spinner />}

      {!loading && error && (
        <S.ErrorMessage>
          No pudimos cargar el historial de pensión. Intenta nuevamente.
        </S.ErrorMessage>
      )}

      {!loading && !error && !hasPensionData && (
        <S.ChartEmpty>
          {needsSetup ? (
            <>
              <S.ChartEmptyTitle>Configura tu pensión para empezar</S.ChartEmptyTitle>
              <S.ChartEmptyHint>
                Con método y valor en Preferencias, aquí verás cómo evoluciona
                tu pensión mes a mes.
              </S.ChartEmptyHint>
              <S.ChartEmptyActions>
                <Button type="button" onClick={goToPreferences} width="auto">
                  Ir a Preferencias
                </Button>
              </S.ChartEmptyActions>
            </>
          ) : (
            <>
              <S.ChartEmptyTitle>Estamos listos. Pronto verás tu historial.</S.ChartEmptyTitle>
              <S.ChartEmptyHint>
                Tu configuración ya está guardada. El historial aparece aquí
                con el primer cálculo.
              </S.ChartEmptyHint>
            </>
          )}
        </S.ChartEmpty>
      )}

      {!loading && !error && hasPensionData && (
        <S.ChartBody>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              key={`pension-chart-${isMobile ? 'm' : 'd'}-${yDomain[0]}-${yDomain[1]}`}
              data={chartData}
              margin={chartMargin}
            >
              <defs>
                <linearGradient id="pensionAreaFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={primary} stopOpacity={0.28} />
                  <stop offset="100%" stopColor={primary} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={grid} strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fill: muted, fontSize: isMobile ? 10 : 11 }}
                tickLine={false}
                axisLine={{ stroke: grid }}
                interval={isMobile ? 1 : 0}
                minTickGap={isMobile ? 12 : 8}
                angle={isMobile ? -35 : 0}
                textAnchor={isMobile ? 'end' : 'middle'}
                height={isMobile ? 48 : 30}
                tickFormatter={isMobile ? shortMonthLabel : undefined}
              />
              <YAxis
                domain={yDomain}
                tickFormatter={
                  isMobile ? formatCompactAxisCurrency : formatAxisCurrency
                }
                tick={{ fill: muted, fontSize: isMobile ? 10 : 11 }}
                tickLine={false}
                axisLine={false}
                width={isMobile ? 44 : 92}
                tickCount={isMobile ? 4 : 5}
                allowDecimals={false}
                allowDataOverflow={false}
              />
              <Tooltip content={<PensionTooltip />} />
              <Area
                type="monotone"
                dataKey="amount"
                stroke={primary}
                strokeWidth={isMobile ? 2 : 2.25}
                fill="url(#pensionAreaFill)"
                connectNulls
                dot={(props) => {
                  const { cx, cy, payload } = props
                  if (!payload?.hasData || cx == null || cy == null) return null
                  const r = isMobile ? 3.5 : 4
                  if (payload.isEstimated) {
                    return (
                      <circle
                        key={`dot-${payload.id || payload.month}`}
                        cx={cx}
                        cy={cy}
                        r={r}
                        fill={theme.colors.background}
                        stroke={primary}
                        strokeWidth={1.75}
                        strokeOpacity={0.7}
                      />
                    )
                  }
                  return (
                    <circle
                      key={`dot-${payload.id || payload.month}`}
                      cx={cx}
                      cy={cy}
                      r={r}
                      fill={primary}
                      strokeWidth={0}
                    />
                  )
                }}
                activeDot={{ r: isMobile ? 4 : 5, strokeWidth: 0, fill: primary }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </S.ChartBody>
      )}
    </S.ChartCard>
  )
}

export default PensionHistory
